import { useState } from 'react';
import * as _ from 'lodash';
import { ToggleSetting } from './ToggleSetting';
import { Icon, Text, Flex, Button, Box } from '@vibe/core';
import { AppModal, AppModalContent } from '@gorilla/ui/src/components/AppModal';
import { Heading } from '@vibe/core';
import { SettingsKnobs } from '@vibe/icons';
import type { Settings } from '@gorilla/widgets-viewer/app/viewer/types';

type AdvancedSettingsModalProps = {
  settings: Settings;
  show: boolean;
  onClose: (newSettings?: Settings) => void;
};

export function AdvancedSettingsModal({ show, onClose, settings }: AdvancedSettingsModalProps) {
  const [settingsDraft, setSettingsDraft] = useState<Settings>(_.cloneDeep(settings));

  return (
    <AppModal
      contentSpacing={false}
      alertDialog={true}
      data-testid="advancedSettingsModal"
      width={300}
      show={show}
      onClose={() => {
        onClose();
      }}
    >
      <AppModalContent>
        <Box marginBottom="large">
          <Flex gap={10} direction="row" justify="start" align="center" style={{ marginTop: '5px', marginBottom: '20px' }}>
            <Icon icon={SettingsKnobs} size={24} />
            <Heading type="h3" weight="bold" align="start" ellipsis style={{ maxWidth: '270px' }}>
              Advanced settings
            </Heading>
          </Flex>
          <Flex gap={8} direction="column" align="start" style={{ width: '100%' }}>
            <div style={{ width: 'inherit' }}>
              <div style={{ marginBottom: '24px' }}>
                <ToggleSetting
                  title="Enable subitems"
                  value={!!settingsDraft.enableSubitemsSupport}
                  onChange={() => {
                    setSettingsDraft({ ...settingsDraft, enableSubitemsSupport: !settingsDraft?.enableSubitemsSupport });
                  }}
                />
                <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '8px' }}>
                  Display collapsible subitems in table layouts. Subitem columns currently can't be hidden or filtered individually. By
                  applying this setting, the app will quickly reload.
                </Text>
              </div>
              <div style={{ marginBottom: '24px' }}>
                <ToggleSetting
                  title="Enable formulas"
                  value={!!settingsDraft.enableFormulaSupport}
                  onChange={() => {
                    setSettingsDraft({ ...settingsDraft, enableFormulaSupport: !settingsDraft?.enableFormulaSupport });
                  }}
                />
                <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '8px' }}>
                  Make formulas available in the column settings. Note that it is only possible to calculate up to 10,000 formulas per
                  minute, it is limited to up to 5 formula columns and formulas that include mirror columns won't work. By applying this
                  setting, the app will quickly reload.
                </Text>
              </div>
              <div style={{ marginBottom: '0px' }}>
                <Box border={true} borderColor="layoutBorderColor" rounded="small" padding="medium" backgroundColor="greyBackgroundColor">
                  <Text type="text2" color="primary" ellipsis={false}>
                    URL parameters
                  </Text>
                  <Text type="text3" color="secondary" ellipsis={false} style={{ marginTop: '8px' }}>
                    Manually add parameters to customize and overwrite specific widget settings. For example, use one widget multiple times,
                    while each embed shows items from a different group.{' '}
                    <a
                      href="https://getgorilla.app/products/board-to-website-widget/docs/advanced-features"
                      target="_blank"
                      style={{ fontSize: 'inherit', lineHeight: 'inherit' }}
                    >
                      Learn more
                    </a>
                  </Text>
                </Box>
              </div>
            </div>
          </Flex>
        </Box>
        <Box marginBottom="small">
          <Flex gap={16}>
            <Button
              size="medium"
              kind="primary"
              color="primary"
              style={{
                width: '100%',
              }}
              onClick={() => {
                onClose(settingsDraft);
              }}
            >
              Apply
            </Button>
          </Flex>
        </Box>
      </AppModalContent>
    </AppModal>
  );
}
